import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI } from '../services/api';
import { Button } from './ds';
import { ModalActions, ModalLead, ModalShell, ModalTitle } from './appstate/ModalShell';

// Shown once when an athlete's Pro trial has run out. Deliberately NOT a
// lockout: with the free tier gone there is no plan to fall back to, but the
// athlete keeps their account, their history and their calendar — they simply
// have no AI coach until they subscribe. Every gated surface still refuses on
// its own; this is the one place that explains WHY, rather than leaving them to
// infer it from a scatter of upsell panels.
//
// `trial_ended` is computed server-side (GET /profile/usage) so the web and
// mobile clients can't drift on the rule.

// Dismissal is remembered per calendar day, not forever: a modal you can
// permanently silence stops doing its job, and one that reappears on every
// navigation is nagging. Once a day is the compromise.
const DISMISS_KEY = 'aithlo:trialEndedDismissed';

const dismissedToday = () => {
  try {
    return localStorage.getItem(DISMISS_KEY) === new Date().toDateString();
  } catch {
    return false; // private mode / blocked storage — better to show it than not
  }
};

const TrialEndedModal = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    if (dismissedToday()) return;
    (async () => {
      try {
        // getUsage() resolves to the payload itself, not an axios response.
        const usage = await profileAPI.getUsage();
        if (!cancelled && usage?.trial_ended) setOpen(true);
      } catch {
        // Never block the app on this — if we can't tell, say nothing.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toDateString()); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return <TrialEndedView onDismiss={dismiss} onPlans={() => { dismiss(); navigate('/settings?tab=plan'); }} />;
};

// Figma "Web · Trial ended": title, one line, Not now and See plans on the
// right. Clicking the dimmed page dismisses it too (as before).
export function TrialEndedView({ onDismiss, onPlans }) {
  return (
    <ModalShell labelledBy="trial-ended-title" width={480} z="z-[10000]" onScrim={onDismiss}>
      <ModalTitle id="trial-ended-title">Your Pro trial has ended</ModalTitle>
      <ModalLead>
        Your calendar and history stay. To keep your coach adapting your plan
        and answering questions, pick a plan.
      </ModalLead>
      <ModalActions>
        <Button kind="text" size="small" onClick={onDismiss}>Not now</Button>
        <Button size="small" onClick={onPlans}>See plans</Button>
      </ModalActions>
    </ModalShell>
  );
}

export default TrialEndedModal;
